import React, { useState, useRef } from 'react'
import { Avatar } from '@/data/avatars'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
} from 'lucide-react'
interface SwipeableAvatarProps {
  avatar: Avatar
  isSelected: boolean
  currentImageIndex: number
  playingAudioId: string | null
  playingVideoId: string | null
  onImageChange: (
    direction: 'next' | 'prev',
    e: React.MouseEvent | React.TouchEvent,
  ) => void
  onAvatarClick: () => void
  onConfirm: (e: React.MouseEvent) => void
  onPlayAudio: (e: React.MouseEvent) => void
  onMediaEnd: () => void
}
export const SwipeableAvatar: React.FC<SwipeableAvatarProps> = ({
  avatar,
  isSelected,
  currentImageIndex,
  playingAudioId,
  playingVideoId,
  onImageChange,
  onAvatarClick,
  onConfirm,
  onPlayAudio,
  onMediaEnd,
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX)
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return
    const touchEnd = e.changedTouches[0].clientX
    const distance = touchStart - touchEnd
    if (Math.abs(distance) > 50) {
      onImageChange(distance > 0 ? 'next' : 'prev', e)
    }
    setTouchStart(null)
  }
  
  const isPlaying = playingAudioId === avatar.id || playingVideoId === avatar.id
  const hasVideoSample = !!avatar.videoSample
  const isPlayingVideo = playingVideoId === avatar.id

  return (
    <div
      className={`relative rounded-lg overflow-hidden transition-all duration-200 cursor-pointer aspect-[3/4] sm:aspect-[9/16] group ${isSelected ? 'ring-2 ring-purple-500 scale-[1.02]' : 'hover:shadow-lg'}`}
      onClick={onAvatarClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {isSelected && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex items-center justify-center z-20">
          <button
            onClick={onConfirm}
            className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors shadow-lg flex items-center gap-2"
          >
            <CheckIcon size={20} />
            Select Avatar
          </button>
        </div>
      )}
      
      <div className="absolute inset-0 bg-gray-900">
        {hasVideoSample && isPlayingVideo ? (
          <video
            ref={videoRef}
            src={avatar.videoSample}
            poster={avatar.images[currentImageIndex]}
            className="w-full h-full object-contain object-center relative z-20"
            onEnded={onMediaEnd}
            autoPlay
            playsInline
          />
        ) : (
          <img
            src={avatar.images[currentImageIndex]}
            alt={avatar.name}
            className="w-full h-full object-contain object-center"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent" />
      </div>
      
      <div
        className="absolute inset-0"
        style={{
          ...(isPlayingVideo 
            ? { backgroundColor: 'black' } 
            : {
                backgroundImage: `url(${avatar.images[currentImageIndex]})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(10px)',
                opacity: 0.5,
                transform: 'scale(1.1)',
              }
          )
        }}
      />
      
      {!isPlayingVideo && (
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={avatar.images[currentImageIndex]}
            alt={avatar.name}
            className="w-full h-full object-contain"
          />
        </div>
      )}
      
      {avatar.images.length > 1 && !isPlayingVideo && (
        <>
          <button
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 rounded-full p-2.5 transition-all opacity-100 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 z-10"
            onClick={(e) => onImageChange('prev', e)}
            aria-label="Previous image"
          >
            <ChevronLeftIcon size={24} className="text-white" />
          </button>
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 rounded-full p-2.5 transition-all opacity-100 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 z-10"
            onClick={(e) => onImageChange('next', e)}
            aria-label="Next image"
          >
            <ChevronRightIcon size={24} className="text-white" />
          </button>
          <div className="absolute top-3 right-3 flex items-center justify-center bg-black/50 rounded-full px-2.5 py-1 z-10">
            <span className="text-white text-sm font-medium">
              {currentImageIndex + 1}/{avatar.images.length}
            </span>
          </div>
        </>
      )}
      
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/50 to-transparent pt-12 pb-4 px-4 z-30">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-medium truncate text-base">
            {avatar.name}
          </h3>
          <button
            className="p-2.5 rounded-full bg-white/25 hover:bg-white/40 text-white transition-all"
            onClick={onPlayAudio}
            aria-label={
              isPlaying
                ? `Pause ${hasVideoSample ? 'video' : 'voice'} sample`
                : `Play ${hasVideoSample ? 'video' : 'voice'} sample`
            }
          >
            {isPlaying ? (
              <PauseIcon size={20} />
            ) : (
              <PlayIcon size={20} />
            )}
          </button>
        </div>
      </div>
      
      {/* Hidden audio element for audio samples */}
      {!hasVideoSample && <audio src={avatar.voiceSample} onEnded={onMediaEnd} />}
    </div>
  )
}

